import { Input } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Task from '../../../types/Task';
const { TextArea } = Input;

export type TitleInputProps = {
  task: Task;
  onClick?: () => void;
  onSave?: (task: Task) => void;
};

export type TitleInputRef = {
  focus: () => void;
};

export const TitleInput = forwardRef<TitleInputRef, TitleInputProps>(({ task, onClick, onSave }, ref) => {
  const [value, setValue] = useState(task.title);
  const [isFocused, setIsFocused] = useState(false);
  const wasFocusedBeforeClickRef = useRef(false);
  const skipOnBlurRef = useRef(false);

  const inputRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
  }));

  useEffect(() => {
    setValue(task.title);
  }, [task.title]);

  useEffect(() => {
    if (isFocused) {
      const handleMouseDown = (e: MouseEvent) => {
        const textarea = inputRef.current?.resizableTextArea?.textArea;
        if (textarea && !textarea.contains(e.target as Node)) {
          e.preventDefault();
          e.stopPropagation();
          document.addEventListener('click', (ce) => ce.stopPropagation(), { once: true, capture: true });
          inputRef.current?.blur();
        }
      };
      document.addEventListener('mousedown', handleMouseDown, true);
      return () => document.removeEventListener('mousedown', handleMouseDown, true);
    }
  }, [isFocused]);

  const updateTaskTitle = (e: any) => {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection && selection.removeAllRanges) {
        selection.removeAllRanges();
      }
    }
    if (skipOnBlurRef.current) {
      skipOnBlurRef.current = false;
      return;
    }

    // Scroll to top after blur
    setTimeout(() => {
      if (inputRef.current) {
        const textarea = inputRef.current.resizableTextArea?.textArea;
        if (textarea) {
          textarea.scrollTop = 0;
        }
      }
    }, 0);

    const newValue = e.target.value;
    const trimmedNewValue = newValue.trim();
    if (!trimmedNewValue) {
      setValue(task.title);
      inputRef.current.blur();
      return;
    }
    if (trimmedNewValue === task.title) {
      inputRef.current.blur();
      return;
    }

    onSave?.({
      ...task,
      title: trimmedNewValue,
    });

    inputRef.current.blur();
  };

  const handleInputChange = (e: any) => {
    setValue(e.target.value);
  };

  return (
    <div className="transform translate-x-[-4px] pt-0.5 pb-1">
      <div>
        <TextArea
          className={`custom-input bg-transparent hover:bg-transparent resize-none w-[230px] ${isFocused ? 'cursor-text' : 'cursor-pointer'}`}
          style={{
            minHeight: '24px',
          }}
          size={'small'}
          variant="filled"
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          autoSize={{ minRows: 1, maxRows: 2 }}
          onFocus={() => {
            setIsFocused(true);
            setTimeout(() => {
              if (inputRef.current) {
                const textarea = inputRef.current.resizableTextArea?.textArea;
                if (textarea) {
                  const textLength = textarea.value.length;
                  textarea.setSelectionRange(textLength, textLength);
                  textarea.scrollTop = textarea.scrollHeight;
                }
              }
            }, 0);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            updateTaskTitle(e);
          }}
          onPointerDown={(e) => {
            // The card's drag is dnd-kit PointerSensor, whose activator is
            // `onPointerDown` — stopping `mousedown` here does nothing, since
            // pointerdown is a separate event that fires first. While renaming,
            // keep it off the card so click-dragging selects text (#2398).
            if (isFocused) {
              e.stopPropagation();
            }
          }}
          onMouseDown={(e) => {
            // Capture focus state before any focus events fire
            wasFocusedBeforeClickRef.current = isFocused;

            // Prevent focus by click - only allow programmatic focus from outside
            // Note: preventDefault stops focus but allows drag events to work
            if (!isFocused) {
              e.preventDefault();
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            // If input was not focused before the click and onClick is provided, trigger navigation
            if (!wasFocusedBeforeClickRef.current && onClick) {
              e.preventDefault();
              onClick();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              updateTaskTitle(e);
            } else if (e.key === 'Escape') {
              setValue(task.title);
              skipOnBlurRef.current = true;
              e.stopPropagation();
              // Scroll to top after escape
              setTimeout(() => {
                if (inputRef.current) {
                  const textarea = inputRef.current.resizableTextArea?.textArea;
                  if (textarea) {
                    textarea.scrollTop = 0;
                  }
                }
              }, 0);
              inputRef.current?.blur();
            }
          }}
          task-title-input-id={task.id}
        />
      </div>
    </div>
  );
});
